import { Dispatch, SetStateAction } from 'react';
import { OutboundProcess } from '@features/supplyChain/types';
import dayjs from 'dayjs';
import {
  XMarkIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { Button } from '../../../tailwindComponents/Button';
import {
  FulfilmentStageTracker,
  latestStageView,
} from './FulfilmentStageTracker';
import { buildOutboundStages } from './outboundStages';
import { CustomerContactDetails } from './CustomerContactDetails';
import {
  getOutboundStatusColor,
  getStatusBadgeClasses,
} from '../utils/statusColors';
import { despatchLineCount } from '../../../utils/despatch';
import { OrderSourceTag } from './OrderSourceTag';

interface WarehouseManagerOutboundSideDrawerProps {
  selectedOutboundProcess: OutboundProcess | null;
  setSelectedOutboundProcess: (payload: OutboundProcess | null) => void;
  selectedOutboundProcessHasDescription: boolean;
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  isDesktop: boolean;
  setIsViewDespatchNoteDialogOpen: Dispatch<SetStateAction<boolean>>;
  setIsAuthoriseForPickingFormOpen: Dispatch<SetStateAction<boolean>>;
  setIsConfirmPickingNoteFormOpen: Dispatch<SetStateAction<boolean>>;
  setIsViewPickingConfirmationDialogOpen: Dispatch<SetStateAction<boolean>>;
  setIsCreateOutboundShipmentNoteFormOpen: Dispatch<SetStateAction<boolean>>;
  setIsViewOutboundShipmentNoteDialogOpen: Dispatch<SetStateAction<boolean>>;
  setIsCreateProofOfDeliveryFormOpen: Dispatch<SetStateAction<boolean>>;
  setIsViewProofOfDeliveryDialogOpen: Dispatch<SetStateAction<boolean>>;
  selectedProcessChildType: string | null;
  setSelectedProcessChildType: (payload: string | null) => void;
}

const childTypeToStageKey: Record<string, string> = {
  despatchNote: 'despatch',
  pickingConfirmation: 'picking',
  shipmentNote: 'shipment',
  goodsReceivedNote: 'delivery',
};

export function WarehouseManagerOutboundSideDrawer({
  selectedOutboundProcess,
  setSelectedOutboundProcess,
  selectedOutboundProcessHasDescription,
  isOpen,
  setIsOpen,
  isDesktop,
  setIsViewDespatchNoteDialogOpen,
  setIsAuthoriseForPickingFormOpen,
  setIsConfirmPickingNoteFormOpen,
  setIsViewPickingConfirmationDialogOpen,
  setIsCreateOutboundShipmentNoteFormOpen,
  setIsViewOutboundShipmentNoteDialogOpen,
  setIsCreateProofOfDeliveryFormOpen,
  setIsViewProofOfDeliveryDialogOpen,
  selectedProcessChildType,
  setSelectedProcessChildType,
}: WarehouseManagerOutboundSideDrawerProps) {
  const onClose = () => {
    setIsOpen(false);
    setSelectedOutboundProcess(null);
    setSelectedProcessChildType(null);
  };

  const getBackdropClasses = () => {
    if (!isOpen) return 'opacity-0 pointer-events-none';
    return isDesktop ? '' : 'bg-black/20';
  };

  const status = selectedOutboundProcess?.status ?? '';
  const description = selectedOutboundProcess?.description;
  const userBusinessName = selectedOutboundProcess?.userBusinessName;
  const notices = selectedOutboundProcess?.notices;
  const despatchNote = selectedOutboundProcess?.despatchNote;

  const statusColor = getOutboundStatusColor(status);
  const despatchLines = despatchNote ? despatchLineCount(despatchNote) : 0;

  const hasIssues = (notices?.length ?? 0) > 0;
  const isProcessComplete =
    !!selectedOutboundProcess?.goodsReceivedNote?.finalisedAt;
  const hasIssuesBeforeProcessIsComplete = hasIssues && !isProcessComplete;

  const closeAndOpen = (open: Dispatch<SetStateAction<boolean>>) => () => {
    setIsOpen(false);
    open(true);
  };

  const stages = selectedOutboundProcess
    ? buildOutboundStages(
        selectedOutboundProcess,
        {
          onViewDespatch: closeAndOpen(setIsViewDespatchNoteDialogOpen),
          onViewPicking: closeAndOpen(setIsViewPickingConfirmationDialogOpen),
          onViewShipment: closeAndOpen(setIsViewOutboundShipmentNoteDialogOpen),
          onViewProofOfDelivery: closeAndOpen(
            setIsViewProofOfDeliveryDialogOpen
          ),
        },
        {
          onAuthorise: closeAndOpen(setIsAuthoriseForPickingFormOpen),
          onConfirmPicking: closeAndOpen(setIsConfirmPickingNoteFormOpen),
          onCreateShipment: closeAndOpen(
            setIsCreateOutboundShipmentNoteFormOpen
          ),
          onCreateProofOfDelivery: closeAndOpen(
            setIsCreateProofOfDeliveryFormOpen
          ),
        }
      )
    : [];

  const footerView = latestStageView(stages) ?? {
    label: 'View Despatch Note',
    onView: closeAndOpen(setIsViewDespatchNoteDialogOpen),
  };

  const initialExpandedKey = selectedProcessChildType
    ? childTypeToStageKey[selectedProcessChildType] ?? null
    : null;

  return (
    <>
      {/* Backdrop - only visible on mobile */}
      <div
        className={`fixed inset-0 z-drawerBackdrop transition-opacity duration-300 ${getBackdropClasses()}`}
        onClick={onClose}
        role='presentation'
      />

      {/* Drawer */}
      <div
        className={`fixed z-drawer bg-white shadow-xl flex flex-col overflow-hidden will-change-transform transition-transform duration-300 ease-out ${
          isDesktop
            ? `right-0 top-0 h-full w-[22rem] border-l border-gray-200 ${
                isOpen ? 'translate-x-0' : 'translate-x-full'
              }`
            : `bottom-0 left-0 right-0 max-h-[90vh] rounded-t-2xl ${
                isOpen ? 'translate-y-0' : 'translate-y-full'
              }`
        }`}
      >
        {/* Header */}
        <div className='flex items-center justify-between px-5 py-4 bg-white'>
          <h2 className='text-xl font-semibold text-gray-900'>Despatch Note</h2>
          <button
            type='button'
            onClick={onClose}
            className='p-1 rounded hover:bg-gray-100 text-gray-400 transition-colors'
            aria-label='Close'
          >
            <XMarkIcon className='w-5 h-5' />
          </button>
        </div>

        {/* Content */}
        <div className='flex-1 overflow-y-auto px-5 py-4'>
          {/* Status Badge */}
          <div className='flex items-center gap-2 mb-6'>
            <span className={getStatusBadgeClasses(statusColor)}>{status}</span>
            <OrderSourceTag
              source={selectedOutboundProcess?.salesOrder?.source}
            />
            {selectedOutboundProcess?.transportOrderId && (
              <span className='inline-flex items-center rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-600/20'>
                Transport Ordered
              </span>
            )}
          </div>

          {/* Basic Details Section */}
          <div className='bg-gray-50 rounded-lg p-4 mb-6'>
            <p className='text-[11px] font-medium text-gray-400 uppercase tracking-wider mb-4'>
              Basic Details
            </p>

            <div className='grid grid-cols-2 gap-x-4 gap-y-3'>
              <div>
                <p className='text-sm font-semibold text-gray-900'>
                  {userBusinessName}
                </p>
                <p className='text-xs text-gray-400'>Customer</p>
              </div>
              <div>
                <p className='text-sm font-semibold text-gray-900'>
                  {despatchNote?.customer?.name ?? despatchNote?.customerName}
                </p>
                <p className='text-xs text-gray-400'>Receiver</p>
              </div>
              <div>
                <p className='text-sm font-semibold text-gray-900'>
                  {despatchNote?.createdAt
                    ? dayjs(despatchNote.createdAt).format('DD/MM/YY')
                    : '-'}
                </p>
                <p className='text-xs text-gray-400'>Creation date</p>
              </div>
              <div>
                <p className='text-sm font-semibold text-gray-900'>
                  {despatchNote?.requiredTimeOfArrival
                    ? dayjs
                        .utc(despatchNote.requiredTimeOfArrival.dateTime)
                        .tz(despatchNote.requiredTimeOfArrival.timeZone)
                        .format('DD/MM/YY')
                    : '-'}
                </p>
                <p className='text-xs text-gray-400'>
                  Requested Time of Arrival
                </p>
              </div>
              <div>
                <p className='text-sm font-semibold text-gray-900'>
                  {despatchNote?.despatchNoteNumber}
                </p>
                <p className='text-xs text-gray-400'>Despatch Note #</p>
              </div>
              <div>
                <p className='text-sm font-semibold text-gray-900'>
                  {despatchLines}
                </p>
                <p className='text-xs text-gray-400'>Lines</p>
              </div>
            </div>
          </div>

          <CustomerContactDetails
            fullAddress={despatchNote?.customer?.fullAddress}
            contactName={despatchNote?.customer?.contactName}
            contactEmail={despatchNote?.customer?.contactEmail}
            contactNumber={despatchNote?.customer?.contactNumber}
          />

          {/* Description */}
          {selectedOutboundProcessHasDescription && (
            <div className={hasIssues ? 'mb-4' : 'mb-6'}>
              <p className='text-[11px] font-medium text-gray-400 uppercase tracking-wider mb-2'>
                Description
              </p>
              <p className='text-sm text-gray-700'>{description}</p>
            </div>
          )}

          {/* Issues */}
          {hasIssues && (
            <div className='mb-6'>
              <p className='text-[11px] font-medium text-gray-400 uppercase tracking-wider mb-2'>
                Issues
              </p>
              <div className='space-y-2'>
                {notices?.map((notice) => (
                  <div
                    key={notice}
                    className={`flex items-center gap-2 p-2 rounded ${
                      hasIssuesBeforeProcessIsComplete
                        ? 'bg-amber-50'
                        : 'bg-red-50'
                    }`}
                  >
                    <ExclamationTriangleIcon
                      className={`w-4 h-4 flex-shrink-0 ${
                        hasIssuesBeforeProcessIsComplete
                          ? 'text-amber-600'
                          : 'text-red-600'
                      }`}
                    />
                    <span
                      className={`text-sm ${
                        hasIssuesBeforeProcessIsComplete
                          ? 'text-amber-800'
                          : 'text-red-800'
                      }`}
                    >
                      {notice}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Fulfilment Progress */}
          {selectedOutboundProcess && (
            <FulfilmentStageTracker
              key={selectedOutboundProcess.id}
              stages={stages}
              initialExpandedKey={initialExpandedKey}
              onCollapse={() => setSelectedProcessChildType(null)}
            />
          )}
        </div>

        {/* Footer */}
        <div className='flex flex-col gap-2 px-5 py-4 border-t border-gray-100 bg-white'>
          <Button
            label={footerView.label}
            intent='primary'
            size='sm'
            className='w-full'
            onClick={footerView.onView}
          />
          <button
            type='button'
            onClick={onClose}
            className='w-full px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors'
          >
            Cancel
          </button>
        </div>
      </div>
    </>
  );
}
